/* eslint-disable react-hooks/exhaustive-deps */
import { Fade, IconButton, Paper, Slider, Stack, Typography } from '@ethings/ui-toolkit';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { useResponsiveText, useSmallScreen } from '../../hooks';
import { Nullable } from '../../types';
import { downloadFile, getSignedUrl } from '../../util';
import { ErrorMessage } from '../error-message';
import { FlexBox } from '../flex-box';
import { Icon } from '../icons';
import { MiddleSpinner } from '../middle-spinner';
import { VideoComponent } from './video-component';
import { videoPlayerStyles } from './video-player.styles';
import { RowButtonProps, VideoPlayerProps } from './video-player.types';

declare global {
  interface HTMLVideoElement {
    webkitEnterFullscreen?: () => void;
  }
  interface Document {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void>;
  }
}

const StyledIconButtonRow = styled(IconButton)({
  padding: '.125em',
  color: '#fff',
  '& svg path': {
    stroke: '#fff',
  },
});

const StyledIconButtonVideoClose = styled(IconButton)(({ theme }) => ({
  color: `${theme.palette.common.white} !important`,
}));

const StyledSlider = styled(Slider, {
  shouldForwardProp: (prop) => prop !== '$smallScreen',
})<{ $smallScreen?: boolean }>(({ $smallScreen, theme }) => ({
  color: `${theme.palette.common.white} !important`,
  height: 8,
  padding: $smallScreen ? '2px 0' : '5px 0',
  '& .MuiSlider-thumb': {
    height: 12,
    opacity: 0,
    transition: 'opacity 150ms',
    width: 12,
    '&:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.16)',
      opacity: 1,
    },
  },
  '& .MuiSlider-track': {
    height: 4,
    transition: 'height 150ms',
  },
  '& .MuiSlider-rail': {
    borderRadius: 2,
    height: 4,
    opacity: 0.4,
    transition: 'height 150ms',
  },
  '&:hover': {
    '& .MuiSlider-rail, & .MuiSlider-track': {
      height: 6,
    },
    '& .MuiSlider-thumb': {
      opacity: 1,
    },
  },
}));

const toHHMMSS = (input: number): string => {
  const appendZero = (val: number): string => val < 10 ? `0${val}` : val.toString();
  const minute = Math.floor((input / 60));
  const second = Math.floor(input) % 60;
  return `${minute}:${appendZero(second)}`;
};

const RowButtons = ({ children, disabled, handler, ...props }: RowButtonProps): JSX.Element => (
  <StyledIconButtonRow data-testid={props['data-testid']} disabled={disabled || false} onClick={handler}>
    {children}
  </StyledIconButtonRow>
);

export const VideoPlayer: FC<VideoPlayerProps> = ({
  controls = {
    close: true,
    export: true,
    fullScreen: true,
    pause: true,
    play: true,
    seek: true,
    volume: true,
  },
  fadeTimer = 500,
  fileName,
  fixedSize = false,
  height = 'auto',
  onExport,
  onFullScreen,
  onModalClick = () => { },
  onMuted = (muted: boolean): void => { if (muted) return; },
  onPause = () => { },
  onPlay = () => { },
  onSeek = (seeked: number) => { if (seeked) return; },
  open = true,
  url,
  width = '100%',
  ...props
}: VideoPlayerProps) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [duration, setDuration] = useState<number>(0);
  const [error, setError] = useState<Nullable<Error>>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [mute, setMute] = useState(true);
  const [play, setPlay] = useState(true);
  const [canPlay, setCanPlay] = useState(false);
  const [position, setPosition] = useState<number>(0);
  const [downloading, setDownloading] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const { restClient } = useAuthenticated();
  const { t } = useTranslation(['common']);
  const { classes } = videoPlayerStyles();
  const smallScreen = useSmallScreen();
  const videoElement = useRef<HTMLVideoElement>(null);
  const videoWrapper = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const video = videoElement.current;
  const [isFullscreenSupported, setIsFullscreenSupported] = useState(false);

  const captionStyles = useResponsiveText('0.75rem', 13, {
    minFontSize: '0.65rem',
    maxFontSize: '0.75rem',
    textLengthThreshold: 15,
  });

  if (!url && !error) setError(new Error(t('common:common.hint.invalid-url')));

  const scheduleHide = (): void => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowControls(false), 3000);
  };

  const handleMouseMove = (): void => {
    setShowControls(true);
    scheduleHide();
  };

  const handleMouseLeave = (): void => {
    clearTimeout(hideTimer.current);
    setShowControls(false);
  };

  const EXPORT_DEFAULT = useCallback(async () => {
    const data = await getSignedUrl(restClient, url);
    if (!data.signedUrl) return setError(new Error(t('common:component.video-modal.hint.download-error')));
    setDownloading(true);
    const videoFile = fileName ?? new URL(data.signedUrl).pathname.replace(/^.*\//gm, '');
    const downloadMessage = enqueueSnackbar(t('common:component.video-player.hint.downloading'), { variant: 'info', persist: true, preventDuplicate: true });
    const handleError = (): void => setError(new Error(t('common:component.video-modal.hint.download-error')));
    const handleCallBack = (): void => { closeSnackbar(downloadMessage); setDownloading(false); };
    downloadFile(data.signedUrl, videoFile, handleError, handleCallBack,
    );
  }, [url]);

  const playVideo = (): void => {
    if (video?.ended) video?.load();
    if (video?.paused && play) {
      video.play().catch(() => {
        setError(new Error(t('common:component.video-modal.hint.player-error')));
      });
    }
  };

  const playHandler = (): void => {
    if (play && controls.play) playVideo();
    else if (controls.pause) video?.pause();
  };

  const muteHandler = (): void => {
    if (!video || !controls.volume) return;
    video.muted = !mute;
    onMuted(!mute);
    setMute(!mute);
  };

  const onVideoSeek = (_: Event, seekTo: number | number[]): void => {
    if (!video || !controls.seek) return;
    void onSeek(typeof seekTo !== 'number' ? seekTo[0] : seekTo);
    video.currentTime = typeof seekTo !== 'number' ? seekTo[0] : seekTo;
  };

  const fullScreen = (): void => {
    if (!video || !controls.fullScreen || !isFullscreenSupported) return;
    const isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
    if (fullscreen && isFullscreen) {
      if (document.exitFullscreen) {
        void document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        void document.webkitExitFullscreen();
      }
    } else if (!fullscreen) {
      if (video.requestFullscreen) {
        void video.requestFullscreen();
      } else if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen?.();
      }
    }
  };

  const onClick = (): void => {
    if (!video) return;
    if (play && controls.play) playVideo();
    else if (!play && controls.pause) video.pause();
  };

  const onVideoPlaying = (): void => { setPlay(false); onPlay(); };
  const onVideoPause = (): void => { setPlay(true); onPause(); };
  const onTimeUpdate = (e: SyntheticEvent): void => {
    const target = e.target as unknown as HTMLMediaElement;
    setPosition(target.currentTime);
  };
  const disableRightClick = (e: SyntheticEvent): void => e.preventDefault();
  const onVideoMetaData = (e: SyntheticEvent): void => {
    const target = e.target as unknown as HTMLMediaElement;
    setError(null);
    setMute(target.muted);
    setDuration(target.duration);
  };

  const errorHandler = (): void => {
    setCanPlay(true);
  };

  const handleVideoExport = (): void => {
    if (downloading) return;
    if (onExport) return onExport(video?.src || url);
    EXPORT_DEFAULT()
      .catch(() => setError(new Error(t('common:component.video-modal.hint.download-error'))));
  };

  useEffect(() => {
    const videoElement = document.createElement('video');
    setIsFullscreenSupported(!!(document.fullscreenEnabled || videoElement.webkitEnterFullscreen));
  }, []);

  useEffect(() => {
    const handleFullscreenChange = (): void => {
      const isNowFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setFullscreen(isNowFullscreen);
      if (onFullScreen) {
        onFullScreen(isNowFullscreen);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [onFullScreen]);

  useEffect(() => {
    if (open) return;
    video?.pause();
  }, [open, video]);

  useEffect(() => {
    if (!error) return;
    enqueueSnackbar(<ErrorMessage error={error} />, { variant: 'error' });
    setError(null);
  }, [error]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const v = videoElement.current;
      if (!v) return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (v.paused) { void v.play().catch(() => { }); } else { v.pause(); }
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault();
        v.currentTime = Math.max(0, v.currentTime - 5);
      } else if (e.code === 'ArrowRight') {
        e.preventDefault();
        v.currentTime = Math.min(v.duration || 0, v.currentTime + 5);
      } else if (e.code === 'KeyM') {
        v.muted = !v.muted;
        setMute(v.muted);
        onMuted(v.muted);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onMuted]);

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  const controlsVisible = showControls || play;

  const VideoContent = (): JSX.Element => <>
    {!canPlay
      && <FlexBox width="640px" height="360px" sx={{ justifyContent: 'center' }}>
        <MiddleSpinner />
      </FlexBox>
    }
    <Fade in={canPlay}>
      <Stack
        ref={videoWrapper}
        className={classes.root}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleMouseMove}
        style={{
          ...((controls.close && (!open || !canPlay)) && { display: 'none' }),
          overflow: 'hidden',
        }}
      >
        {controls.close
          && <Paper className={classes.actionCard} elevation={1}>
            <StyledIconButtonVideoClose
              aria-label={t('common:common.action.close')}
              onClick={onModalClick}
              size="small"
              data-testid='video-player-close'
            >
              <Icon data-testid="close-icon" icon="CloseIcon" />
            </StyledIconButtonVideoClose>
          </Paper>
        }
        <VideoComponent
          key={url}
          ref={videoElement}
          autoPlay={true}
          className={classes.video}
          fixedSize={fixedSize}
          fullscreen={fullscreen}
          muted={true}
          onCanPlay={() => setCanPlay(true)}
          onClick={onClick}
          onContextMenu={disableRightClick}
          onDoubleClick={fullScreen}
          onError={errorHandler}
          onLoadedMetadata={onVideoMetaData}
          onPause={onVideoPause}
          onPlaying={onVideoPlaying}
          onTimeUpdate={onTimeUpdate}
          playsInline={true}
          preload="metadata"
          src={url}
          data-testid={props['data-testid']}
        />
        <div
          className={classes.videoPlayerControllers}
          style={{
            opacity: controlsVisible ? 1 : 0,
            transition: `opacity linear ${Math.max(0, fadeTimer)}ms`,
          }}
        >
          <div style={{ width: '100%' }}>
            <StyledSlider
              aria-label={t('common:component.video-player.hint.time-label')}
              $smallScreen={smallScreen}
              componentsProps={{
                root: {
                  style: { overflow: 'hidden' },
                },
              }}
              defaultValue={0}
              disabled={error !== null || !controls.seek}
              max={duration}
              min={0}
              onChange={onVideoSeek}
              value={position}
            />
          </div>
          <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ alignItems: 'center', display: 'flex', gap: '0.25em' }}>
              <RowButtons
                disabled={!(controls.play && controls.pause)}
                handler={playHandler}
                data-testid="video-player-play"
              >
                {play ? <Icon icon="Play" /> : <Icon icon="Pause" />}
              </RowButtons>
              <RowButtons disabled={!controls.volume} handler={muteHandler} data-testid="video-player-mute">
                {mute ? <Icon icon="VolumeOff" /> : <Icon icon="Volume2" />}
              </RowButtons>
              <Typography
                variant="caption"
                style={{
                  color: '#fff',
                  fontSize: captionStyles.fontSize,
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: captionStyles.lineHeight,
                  marginLeft: '0.25em',
                  overflow: captionStyles.overflow,
                  textOverflow: captionStyles.textOverflow,
                  whiteSpace: 'nowrap',
                  wordBreak: captionStyles.wordBreak,
                }}
              >
                {`${toHHMMSS(position)} / ${toHHMMSS(duration)}`}
              </Typography>
            </div>
            <div style={{ alignItems: 'center', display: 'flex', gap: '0.25em' }}>
              {!smallScreen
                && <RowButtons disabled={downloading ?? !controls.export} handler={handleVideoExport} data-testid="video-player-download">
                  {downloading
                    ? <MiddleSpinner size={20} />
                    : <Icon icon="Download" />
                  }
                </RowButtons>
              }
              <RowButtons disabled={!controls.fullScreen || !isFullscreenSupported} handler={fullScreen} data-testid="video-player-maximize">
                {fullscreen ? <Icon icon="MinimizeAlt" /> : <Icon icon="Maximize" />}
              </RowButtons>
            </div>
          </div>
        </div>
      </Stack>
    </Fade>
  </>;

  const VideoContentMemo = useMemo(VideoContent, [
    canPlay,
    captionStyles,
    classes,
    controls,
    controlsVisible,
    disableRightClick,
    downloading,
    duration,
    error,
    fadeTimer,
    fullScreen,
    fullscreen,
    handleMouseLeave,
    handleMouseMove,
    height,
    isFullscreenSupported,
    mute,
    muteHandler,
    onClick,
    onModalClick,
    onPause,
    onplaying,
    onTimeUpdate,
    onVideoMetaData,
    play,
    playHandler,
    position,
    smallScreen,
    url,
    video,
    videoElement,
    videoWrapper,
    width,
  ]);

  return VideoContentMemo;
};
